import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import Grid from '@mui/material/Grid'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { useDispatch, useSelector } from 'react-redux'
import { IBankAccount } from '@sparling/financial-common'
import { Typography } from '@mui/material'
import { GreenButton } from '../../../../../../../components/Dashboard/Buttons/GreenButton'
import { TableComponent } from '../../../../../../../components/Dashboard/Table'
import { icons } from '../../../../../../../constants/icons'
import { columns } from './BankAccountsList'
import { BankAccountsModal } from './BankAccountsModal'
import { userBankAccountsSelector } from '../../../../../../../store/selectors'
import { readUserBankAccountsRequest } from '../../../../../../../store/actions'
import { RemoveConfirmationModal } from './RemoveConfirmationModal'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    height: '100%',
  },
  titleWrap: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    minWidth: 400,
  },
  table: {
    paddingTop: theme.spacing(2),
  },
  buttonWrap: {
    justifyContent: 'flex-end',
  },
}))

export const BankAccounts: FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [isSubmit, setIsSubmit] = useState<boolean>(true)
  const { data, status } = useSelector(userBankAccountsSelector)
  const [isOpenAccountModal, setOpenAccountModal] = useState<boolean>(false)
  const [isOpenRemoveConfirmationModal, setIsOpenRemoveConfirmationModal] = useState<boolean>(false)
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null)
  const [shouldFieldsBeDisabled, setShouldFieldsBeDisabled] = useState<boolean>(false)

  const loadAccountsData = useCallback(
    (page = 1) => {
      dispatch(
        readUserBankAccountsRequest({
          page,
        }),
      )
    },
    [dispatch],
  )

  useEffect(() => {
    loadAccountsData()
  }, [loadAccountsData])

  const getSelectedElementIdHandle = useCallback((id) => {
    setSelectedAccountId(id)
  }, [])

  const openBankAccountModalHandle = useCallback(() => {
    setIsSubmit(true)
    setOpenAccountModal(true)
    setSelectedAccountId(null)
    setShouldFieldsBeDisabled(false)
  }, [])

  const showBankAccountModalHandle = useCallback(() => {
    openBankAccountModalHandle()
    setIsSubmit(false)
    setShouldFieldsBeDisabled(true)
  }, [openBankAccountModalHandle])

  const openRemoveConfirmationBankAccountModalHandle = useCallback(() => {
    setIsOpenRemoveConfirmationModal(true)
    setSelectedAccountId(null)
  }, [])

  const accountModalTitle = useMemo(() => {
    if (!isSubmit) {
      return 'Bank Account Details'
    }

    return !selectedAccountId ? 'Add Bank Account' : 'Edit Bank Account'
  }, [isSubmit, selectedAccountId])

  const closeBankAccountModalHandle = useCallback(() => setOpenAccountModal(false), [])
  const closeRemoveConfirmationBankAccountModalHandle = useCallback(() => setIsOpenRemoveConfirmationModal(false), [])

  return (
    <Grid container display={'flex'}>
      <Grid container className={classes.container}>
        <Grid container className={classes.titleWrap}>
          <Grid item xs={8} display="flex">
            <Typography variant={'h6'}>Bank Accounts</Typography>
          </Grid>
          <Grid item xs={4} display="flex" justifyContent={'flex-end'}>
            <GreenButton
              width={140}
              prefix={icons.ADD}
              ml={8}
              text={'Add Account'}
              onClick={openBankAccountModalHandle}
            />
          </Grid>
        </Grid>
        <Grid container className={classes.table}>
          <TableComponent
            id={'client-bank-accounts'}
            page={data.meta.currentPage}
            getUserId={getSelectedElementIdHandle}
            columns={columns}
            data={data.items}
            params={data.meta}
            onShow={showBankAccountModalHandle}
            onEdit={openBankAccountModalHandle}
            onDelete={openRemoveConfirmationBankAccountModalHandle}
            loading={status === 'LOADING'}
            loadData={loadAccountsData}
          ></TableComponent>
        </Grid>
      </Grid>
      {isOpenAccountModal && (
        <BankAccountsModal
          title={accountModalTitle}
          isOpen={true}
          isSubmit={isSubmit}
          modalClose={closeBankAccountModalHandle}
          fields={data.items.find((item) => item.id === selectedAccountId) as IBankAccount}
          shouldFieldsBeDisabled={shouldFieldsBeDisabled}
        />
      )}
      <RemoveConfirmationModal
        account={data.items.find((item) => item.id === selectedAccountId) as IBankAccount}
        isOpen={isOpenRemoveConfirmationModal}
        onClose={closeRemoveConfirmationBankAccountModalHandle}
      />
    </Grid>
  )
}
